import React, { useState, useRef, useEffect } from 'react';
import { Upload, X, AlertCircle, Check } from 'lucide-react';

interface CompanyLogoUploaderProps {
  currentLogoUrl?: string | null;
  onUpload: (file: File) => Promise<void>;
  onDelete?: () => Promise<void>;
  disabled?: boolean;
  token?: string | null;
}

export const CompanyLogoUploader: React.FC<CompanyLogoUploaderProps> = ({
  currentLogoUrl,
  onUpload,
  onDelete,
  disabled = false,
  token
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [logoDataUrl, setLogoDataUrl] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(currentLogoUrl || null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch logo as data URL when currentLogoUrl changes
  useEffect(() => {
    const fetchLogo = async () => {
      if (!currentLogoUrl) {
        setLogoDataUrl(null);
        return;
      }

      // If it's already a data URL, use it directly
      if (currentLogoUrl.startsWith('data:')) {
        setLogoDataUrl(currentLogoUrl);
        return;
      }

      // Don't fetch if no token available (prevents 401 errors)
      if (!token) {
        console.warn('No auth token available for logo fetch');
        setLogoDataUrl(null);
        return;
      }

      // If it's a relative path (streaming endpoint), fetch it with auth token
      if (!currentLogoUrl.startsWith('http')) {
        try {
          const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
          const url = `${apiUrl}${currentLogoUrl}`;
          const response = await fetch(url, {
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
          });

          if (response.ok) {
            const blob = await response.blob();
            const reader = new FileReader();
            reader.onloadend = () => {
              setLogoDataUrl(reader.result as string);
            };
            reader.readAsDataURL(blob);
          }
        } catch (err) {
          console.error('Failed to fetch logo:', err);
        }
        return;
      }

      // For absolute URLs, just use them directly
      setLogoDataUrl(currentLogoUrl);
    };

    fetchLogo();
    setPreviewUrl(currentLogoUrl || null);
  }, [currentLogoUrl, token]);

  const handleFileSelect = async (file: File) => {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target?.result as string);
    };
    reader.readAsDataURL(file);

    // Upload file
    setIsUploading(true);
    setError(null);
    setSuccess(false);

    try {
      await onUpload(file);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to upload logo');
      setPreviewUrl(currentLogoUrl || null);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    if (disabled) return;

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;

    if (window.confirm('Are you sure you want to delete the company logo?')) {
      setIsUploading(true);
      setError(null);

      try {
        await onDelete();
        setPreviewUrl(null);
        setLogoDataUrl(null);
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      } catch (err: any) {
        setError(err.message || 'Failed to delete logo');
      } finally {
        setIsUploading(false);
      }
    }
  };

  return (
    <div className="space-y-4">
      {/* Preview or Upload Area */}
      {logoDataUrl ? (
        <div className="relative">
          <div className="border-2 border-gray-300 dark:border-gray-600 rounded-lg p-4 bg-white dark:bg-gray-800">
            <img
              src={logoDataUrl}
              alt="Company Logo"
              className="max-h-48 max-w-full mx-auto object-contain"
            />
          </div>
          {onDelete && !disabled && (
            <button
              onClick={handleDelete}
              disabled={isUploading}
              className="absolute top-2 right-2 p-2 bg-red-500 hover:bg-red-600 text-white rounded-full shadow-lg disabled:opacity-50"
              title="Delete logo"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      ) : (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => !disabled && fileInputRef.current?.click()}
          className={`
            border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all
            ${isDragging
              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
              : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
            }
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            <span className="font-semibold text-blue-500">Click to upload</span> or drag and drop
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-500">
            PNG, JPG, GIF, SVG up to 10MB
          </p>
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileInputChange}
        className="hidden"
        disabled={disabled}
      />

      {/* Status messages */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}

      {success && (
        <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
          <p className="text-sm text-green-700 dark:text-green-400">
            {previewUrl ? 'Logo uploaded successfully!' : 'Logo deleted successfully!'}
          </p>
        </div>
      )}

      {isUploading && (
        <div className="flex items-center justify-center gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent"></div>
          <p className="text-sm text-blue-700 dark:text-blue-400">
            {previewUrl ? 'Uploading...' : 'Deleting...'}
          </p>
        </div>
      )}
    </div>
  );
};
